import { useCallback, useEffect, useState } from "react";
import { Pagination } from "../screens/ScreenCard";
import { ApolloQueryResult, FetchMoreQueryOptions } from "@apollo/client";
import { PageInfo } from "../__generated__/graphql";
import { useSnackbar } from "./snackbar";
import { useI18n } from "./i18n-hooks";

// Before using usePagination with GraphQL call,
// make sure that you added a "merge" function for your call in `graphql.config.js`

type UsePaginationParams = {
    fetchMore: (options: FetchMoreQueryOptions<{ offset: number }>) => Promise<
        ApolloQueryResult<{
            [k in string]: { elements: never[]; pageInfo: PageInfo };
        }>
    >;
};

export const usePagination = ({ fetchMore: fetchMoreProps }: UsePaginationParams) => {
    const [isLoadingData, setIsLoadingData] = useState(false);
    const [hasNextPage, setHasNextPage] = useState(false);
    const [nextOffset, setNextOffset] = useState(0);
    const { showSnackbar } = useSnackbar();
    const i18n = useI18n();

    const fetchMore = useCallback(async () => {
        try {
            setIsLoadingData(true);
            const newResult = await fetchMoreProps({ variables: { offset: nextOffset } });
            const key = Object.keys(newResult.data)[0];
            setHasNextPage(newResult.data[key].pageInfo.hasNextPage);
            setNextOffset(newResult.data[key].pageInfo.nextOffset || 0);
        } catch (e) {
            showSnackbar({
                text: i18n.t("errors.pagination_error"),
                mode: "error",
            });
        } finally {
            setIsLoadingData(false);
        }
    }, [fetchMoreProps, i18n, nextOffset, showSnackbar]);

    useEffect(() => {
        void fetchMore();
        // Only necessary on initial load to fill up `hasNextPage` and `nextOffset`
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return { isLoading: isLoadingData, fetchMore, hasNextPage } as Pagination;
};
